import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import clinic1 from "../assets/clinic1.avif";
import clinic2 from "../assets/clinic2.avif";

/**
 * Home.js — AV Medical Hospital (final design)
 *
 * - Uses clinic1.avif and clinic2.avif from src/assets/
 * - Smooth crossfade hero background (images)
 * - Main heading: "AV Medical Hospital"
 * - Services: includes "Diseases in Pregnancy"
 * - Rating: 4.9 ★ on Justdial (linked)
 * - Patient Reviews (NEW SECTION)
 * - FAQ accordion below Services
 * - Subtle animations and responsive layout
 *
 * Paste this file into: src/pages/Home.js
 */

const JUSTDIAL_URL =
  "https://www.justdial.com/Meerut/AV-Medical-and-Ultrasound-Centre-Dr-Vikash-Dr-Sweta-Modipuram/9999PX121-X121-250203152903-K5W4_BZDET";

const services = [
  "General Physician Consultations",
  "Pathology Lab Testing",
  "Vaccinations",
  "Health Check-ups",
  "Diseases in Pregnancy",
  "Minor Emergency Care",
];

// --- NEW: Reviews Data ---
const reviews = [
  {
    stars: "⭐⭐⭐⭐✰",
    text: "I can’t thank you enough for the special, excellent care you have provided and the unique gift you are to your patients. Thank you so much for the great care and surgery you performed on me. And also thanks to all staff.",
    author: "Kuldeep",
    date: "07 Aug 2024",
  },
  {
    stars: "⭐⭐⭐⭐⭐",
    text: "Excellent. Doctor is very nice and friendly. He spends a lot of time during investigation.",
    author: "Bharat Bhushan",
    date: "17 Jun 2025",
  },
  {
    stars: "⭐⭐⭐⭐⭐",
    text: "Great service and care!",
    author: "Birpal Singh",
    date: "27 Sept 2024",
  },
];
// -------------------------

const faqs = [
  {
    q: "Is A V Medical Centre clean and hygienic?",
    a:
      "Almost all hospitals maintain high-level cleanliness to keep patients safe from any infection. For more information on the cleanliness maintained at A V Medical Centre, you can check the customer ratings and reviews or visit the hospital in person.",
  },
  {
    q: "Do hospitals provide ambulance services?",
    a:
      "Yes, most hospitals provide ambulance services for transporting patients to the hospital and back. However, it is recommended to check with A V Medical Centre for further information on this.",
  },
  {
    q: "Can I visit a patient any time of the day in a hospital?",
    a:
      "No, there are fixed visiting hours at hospitals. You can contact the A V Medical Centre for visiting hours.",
  },
];

function Home() {
  const images = [clinic1, clinic2];
  const [current, setCurrent] = useState(0);
  const [faqOpen, setFaqOpen] = useState(Array(faqs.length).fill(false));

  useEffect(() => {
    const id = setInterval(() => {
      setCurrent((p) => (p + 1) % images.length);
    }, 4500); // 4.5s interval
    return () => clearInterval(id);
  }, [images.length]);

  const toggleFaq = (i) => {
    setFaqOpen((prev) => {
      const copy = [...prev];
      copy[i] = !copy[i];
      return copy;
    });
  };

  return (
    <div style={styles.pageWrap}>
      {/* CSS block for animations & small reusable classes */}
      <style>{globalCSS}</style>

      {/* HERO: full-width crossfade background with overlay */}
      <header style={styles.heroContainer} aria-hidden="false">
        {images.map((src, idx) => (
          <img
            key={idx}
            src={src}
            alt={`AV Medical Hospital slide ${idx + 1}`}
            style={{
              ...styles.heroImage,
              opacity: idx === current ? 1 : 0,
              transform: idx === current ? "scale(1.02)" : "scale(1)",
            }}
            onError={(e) => {
              // if any error, set opacity to 0 for that image so fallback not required here
              e.currentTarget.style.display = "none";
            }}
          />
        ))}

        {/* gentle overlay to ensure text contrast */}
        <div style={styles.heroOverlay} />

        {/* Hero text (centered) */}
        <div style={styles.heroInner}>
          <div style={styles.heroCard}>
            <h1 style={styles.title}>AV Medical and Ultrasound Centre</h1>

            <div style={styles.ratingRow}>
              <div style={styles.starPill}>4.9★</div>
              <div style={{ marginLeft: 12 }}>
                <div style={styles.trustedText}>Trusted — 4.9 / 5 on{" "}
                  <a href={JUSTDIAL_URL} target="_blank" rel="noreferrer" style={styles.jdLink}>
                    Justdial
                  </a>
                </div>
                <div style={styles.smallMuted}>Comprehensive care • Diagnostics • Ultrasound</div>
              </div>
            </div>

            <p style={styles.heroSubtitle}>
              Serving Meerut with compassionate, modern healthcare — open 24×7 for emergency care.
            </p>
            <p style={styles.heroSubtitle}>
              Opposite Water Tank, Pallav Puram Phase 2, Meerut - 250001
            </p>

            <div style={styles.heroCtaRow}>
              <Link to="/book-appointment" style={styles.primaryCta}>
                Book Appointment
              </Link>
              <a href={`tel:+919412745627`} style={styles.ghostCta}>
                Call: +91 94127 45627
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* ABOUT + QUICK INFO */}
      <section id="about" style={styles.section}>

        <div style={styles.container}>
          <div style={styles.row}>
            <div style={styles.leftColumn}>
              <h2 style={styles.sectionTitle}>About AV Medical Centre</h2>
              <p style={styles.lead}>
                AV Medical Hospital in Pallav Puram Phase 2, Meerut offers a wide range of clinical and diagnostic
                services with a focus on patient comfort and timely care. We provide general physician consultations,
                pathology testing, vaccinations, health check-ups, specialized care in pregnancy-related conditions,
                and minor emergency treatments. Established in 03-2018
              </p>

              <div style={styles.quickFacts}>
                <div style={styles.infoCard}>
                  <strong style={styles.infoNumber}>4.9★</strong>
                  <small style={styles.infoLabel}>Rated on Justdial</small>
                </div>

                <div style={styles.infoCard}>
                  <strong style={styles.infoNumber}>24×7</strong>
                  <small style={styles.infoLabel}>Emergency availability</small>
                </div>

                <div style={styles.infoCard}>
                  <strong style={styles.infoNumber}>+91 94127 45627</strong>
                  <small style={styles.infoLabel}>Contact</small>
                </div>
              </div>
            </div>

            <aside style={styles.rightColumn}>
              <div style={styles.locationCard}>
                <h4 style={{ margin: 0, color: "#0f172a" }}>Location</h4>
                <p style={{ marginTop: 8, color: "#374151", lineHeight: 1.4 }}>
                  Opposite Water Tank, Pallav Puram Phase 2, Meerut - 250001
                </p>
                <a
                  href={`https://www.google.com/maps/search/${encodeURIComponent("Opposite Water Tank, Pallav Puram Phase 2, Meerut - 250001")}`}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.mapLink}
                >
                  Open in Google Maps
                </a>

                {/* --- MINI MAP INSERTED BELOW (inside the Location card) --- */}
                <div style={{ marginTop: 12, borderRadius: 10, overflow: "hidden" }}>
                  <iframe
                    title="AV Medical Hospital Mini Map"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3475.317869583724!2d77.66814007498284!3d28.99187307543332!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390c64865b97a6f7%3A0x43e4ce9d8217e0c5!2sA%20V%20Medical%20Centre!5e0!3m2!1sen!2sin!4v1699373930005!5m2!1sen!2sin"
                    width="100%"
                    height="200"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
                {/* --- END MINI MAP --- */}

              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section style={{ ...styles.section, backgroundColor: "#fff" }}>
        <div style={styles.container}>
          <h3 style={styles.sectionTitle}>Why Patients Choose AV Medical Hospital</h3>
          <div style={styles.cardsRow}>
            <div style={styles.featureCard} className="featureCardClass">
              <h4 style={styles.featureTitle}>Experienced Doctors</h4>
              <p style={styles.featureText}>
                Qualified physicians including specialists in general medicine and ultrasound — dedicated to accurate diagnosis and patient comfort.
              </p>
            </div>

            <div style={styles.featureCard} className="featureCardClass">
              <h4 style={styles.featureTitle}>Comprehensive Services</h4>
              <p style={styles.featureText}>
                Diagnostics, pathology testing, vaccinations, pregnancy care and minor emergency services — all coordinated for smooth patient journeys.
              </p>
            </div>

            <div style={styles.featureCard} className="featureCardClass">
              <h4 style={styles.featureTitle}>Convenient Location & Hours</h4>
              <p style={styles.featureText}>
                Centrally located in Pallav Puram Phase 2 with accessible timing information and 24×7 emergency support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section style={styles.section}>
        <div style={styles.container}>
          <h3 style={styles.sectionTitle}>Our Services</h3>
          <div style={styles.servicesGrid}>
            {services.map((s, i) => (
              <div key={i} style={styles.serviceCard}>
                <strong style={{ color: "#0f172a" }}>{s}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- NEW: PATIENT REVIEWS SECTION --- */}
      <section style={{ ...styles.section, backgroundColor: "#f0f9ff" }}>
        <div style={styles.container}>
          <h3 style={styles.sectionTitle}>What Our Patients Say</h3>
          <div style={{...styles.cardsRow, alignItems: 'stretch', marginTop: 16}}>
            {reviews.map((review, i) => (
              // Applied the new class 'reviewCardClass' here for the hover effect
              <div key={i} style={styles.reviewCard} className="reviewCardClass"> 
                <div style={styles.reviewHeader}>
                  <span style={styles.reviewStars}>{review.stars}</span>
                  <span style={styles.reviewDate}>{review.date}</span>
                </div>
                <p style={styles.reviewText}>"{review.text}"</p>
                <p style={styles.reviewAuthor}>– {review.author}</p>
              </div>
            ))}
          </div>
          <p style={{ textAlign: 'center', marginTop: 24, fontSize: '1.1rem', fontWeight: 600, color: '#1e3a8a' }}>
            <span style={{ fontSize: '1.5rem', marginRight: 8 }}>⭐</span> 4.9 / 5 Overall Rating
          </p>
        </div>
      </section>
      {/* ------------------------------------- */}

      {/* FAQ (accordion) */}
      <section style={{ ...styles.section, backgroundColor: "#f8fafc" }}>
        <div style={styles.container}>
          <h3 style={styles.sectionTitle}>Frequently Asked Questions</h3>
          <div style={styles.faqWrap}>
            {faqs.map((f, i) => (
              <div key={i} style={styles.faqItem}>
                <button
                  onClick={() => toggleFaq(i)}
                  style={{ ...styles.faqQ, ...(faqOpen[i] ? styles.faqQOpen : {}) }}
                  aria-expanded={!!faqOpen[i]}
                >
                  <span>{f.q}</span>
                  <span style={styles.faqChevron}>{faqOpen[i] ? "−" : "+"}</span>
                </button>
                <div
                  style={{
                    ...styles.faqA,
                    maxHeight: faqOpen[i] ? "400px" : 0,
                    padding: faqOpen[i] ? "12px 18px" : "0 18px",
                    opacity: faqOpen[i] ? 1 : 0,
                  }}
                >
                  <p style={{ margin: 0, color: "#374151", lineHeight: 1.6 }}>{f.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer-like small CTA */}
      <section style={{ padding: "34px 20px", background: "#fff" }}>
        <div style={styles.container}>
          <div style={styles.footerCta}>
            <div>
              <h4 style={{ margin: 0, color: "#0f172a" }}>Ready to visit AV Medical Hospital?</h4>
              <p style={{ marginTop: 8, color: "#475569" }}>
                Book an appointment online or call us at <strong>+91 94127 45627</strong>.
              </p>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <Link to="/book-appointment" style={styles.primaryCta}>
                Book Appointment
              </Link>
              <a href={`tel:+919412745627`} style={styles.ghostCta}>
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------- Styles and CSS ---------- */

const styles = {
  pageWrap: {
    fontFamily:
      "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
    color: "#0f172a",
    lineHeight: 1.6,
  },
  heroContainer: {
    position: "relative",
    height: "70vh",
    width: "100%",
    overflow: "hidden",
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  heroImage: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "opacity 1.2s ease-in-out, transform 6s ease",
    filter: "brightness(0.55)",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(180deg, rgba(14,42,80,0.32), rgba(6,21,45,0.28))",
    pointerEvents: "none",
  },
  heroInner: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 20px",
  },
  heroCard: {
    maxWidth: 980,
    textAlign: "center",
    color: "#fff",
  },
  title: {
    fontSize: "3.2rem",
    margin: 0,
    fontWeight: 800,
    letterSpacing: "0.6px",
  },
  ratingRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: 14,
  },
  starPill: {
    background: "linear-gradient(90deg,#ffd54a,#ffb300)",
    color: "#111827",
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 800,
    boxShadow: "0 10px 24px rgba(37,99,235,0.12)",
    fontSize: "1rem",
  },
  trustedText: {
    fontWeight: 600,
    color: "#fff",
  },
  smallMuted: {
    color: "rgba(255,255,255,0.85)",
    fontSize: 13,
  },
  heroSubtitle: {
    marginTop: 16,
    marginBottom: 18,
    maxWidth: 760,
    marginLeft: "auto",
    marginRight: "auto",
    fontSize: "1.05rem",
    color: "rgba(255,255,255,0.95)",
  },
  heroCtaRow: {
    display: "flex",
    gap: 12,
    justifyContent: "center",
    marginTop: 6,
    flexWrap: "wrap",
  },
  primaryCta: {
    backgroundColor: "#fff",
    color: "#1e3a8a",
    padding: "10px 18px",
    borderRadius: 8,
    fontWeight: 700,
    textDecoration: "none",
    boxShadow: "0 8px 30px rgba(16,24,40,0.12)",
  },
  ghostCta: {
    color: "#fff",
    padding: "10px 18px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.18)",
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
  },
  section: {
    padding: "44px 20px",
  },
  container: {
    maxWidth: 1100,
    margin: "0 auto",
  },
  row: {
    display: "flex",
    gap: 28,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  leftColumn: {
    flex: "1 1 420px",
    minWidth: 280,
  },
  rightColumn: {
    flex: "0 1 300px",
    minWidth: 260,
  },
  sectionTitle: {
    color: "#1e40af",
    fontSize: 26,
    marginBottom: 12,
    textAlign: 'center', // Added for the new review section
  },
  lead: {
    color: "#374151",
    lineHeight: 1.6,
  },
  quickFacts: {
    display: "flex",
    gap: 12,
    marginTop: 16,
    flexWrap: "wrap",
  },
  infoCard: {
    padding: "10px 14px",
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 6px 18px rgba(16,24,40,0.06)",
  },
  infoNumber: {
    display: "block",
    color: "#0f172a",
    fontWeight: 700,
  },
  infoLabel: {
    color: "#6b7280",
    fontSize: 12,
  },
  locationCard: {
    background: "#fff",
    padding: 18,
    borderRadius: 10,
    boxShadow: "0 8px 24px rgba(16,24,40,0.06)",
  },
  mapLink: {
    color: "#2563eb",
    textDecoration: "none",
    fontWeight: 600,
  },
  cardsRow: {
    display: "flex",
    gap: 20,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  featureCard: {
    background: "#fff",
    borderRadius: 10,
    padding: 20,
    width: 320,
    boxShadow: "0 8px 22px rgba(2,6,23,0.04)",
    transition: "transform 0.25s ease",
  },
  featureTitle: {
    marginTop: 0,
    color: "#0f172a",
  },
  featureText: {
    color: "#475569",
  },
  servicesGrid: {
    display: "flex",
    gap: 16,
    justifyContent: "center",
    flexWrap: "wrap",
  },
  serviceCard: {
    background: "#fff",
    padding: 14,
    borderRadius: 8,
    boxShadow: "0 8px 20px rgba(2,6,23,0.04)",
    minWidth: 220,
    textAlign: "center",
  },
  // --- NEW: Review Card Styles ---
  reviewCard: {
    background: "#fff",
    borderRadius: 10,
    padding: 20,
    width: 320,
    boxShadow: "0 8px 22px rgba(2,6,23,0.04)",
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform 0.25s ease, box-shadow 0.25s ease', // Added transition for smooth effect
  },
  reviewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
    paddingBottom: 8,
    borderBottom: '1px solid #e2e8f0',
  },
  reviewStars: {
    fontSize: '1.2rem',
  },
  reviewDate: {
    fontSize: 12,
    color: '#64748b',
  },
  reviewText: {
    fontStyle: 'italic',
    color: '#374151',
    lineHeight: 1.5,
    margin: '10px 0',
    flexGrow: 1,
  },
  reviewAuthor: {
    fontWeight: 600,
    color: '#0f172a',
    textAlign: 'right',
    marginTop: 10,
  },
  // -------------------------------
  faqWrap: {
    maxWidth: 800,
    margin: "12px auto 0 auto",
  },
  faqItem: {
    marginBottom: 12,
    borderRadius: 10,
    overflow: "hidden",
    boxShadow: "0 6px 20px rgba(2,6,23,0.03)",
    background: "#fff",
  },
  faqQ: {
    width: "100%",
    padding: "14px 18px",
    textAlign: "left",
    background: "#fff",
    border: "none",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    fontSize: 16,
    color: "#0f172a",
  },
  faqQOpen: {
    background: "#eef2ff",
  },
  faqChevron: {
    fontWeight: 700,
    color: "#2563eb",
    marginLeft: 12,
  },
  faqA: {
    overflow: "hidden",
    transition: "all 0.35s ease",
  },
  footerCta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 20,
    flexWrap: "wrap",
    background: "#f8fafc",
    padding: 18,
    borderRadius: 10,
  },
  jdLink: {
    color: "#ffd700",
    textDecoration: "underline",
  },
};

/* Minimal global CSS for small animations and responsiveness */
const globalCSS = `
  @media (max-width: 900px) {
    .heroTitle { font-size: 2rem !important; }
  }

  /* slight pulse for star pill */
  @keyframes pulseStar {
    0% { box-shadow: 0 6px 18px rgba(37,99,235,0.08); transform: translateY(0) scale(1); }
    50% { box-shadow: 0 12px 30px rgba(37,99,235,0.12); transform: translateY(-2px) scale(1.02); }
    100% { box-shadow: 0 6px 18px rgba(37,99,235,0.08); transform: translateY(0) scale(1); }
  }

  .starPillClass {
    animation: pulseStar 3s ease-in-out infinite;
  }

  /* hover lift for feature cards */
  .featureCardClass:hover {
    transform: translateY(-6px);
  }
  
  /* hover lift for review cards (NEW) */
  .reviewCardClass:hover {
    transform: translateY(-6px);
    box-shadow: 0 14px 30px rgba(2,6,23,0.1);
  }
`;

/* Add classes to some inline styles (where needed) via small wrappers (applied by style prop) */

export default Home;